import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Linkedin,
  MessageCircle,
  Send,
  Mail,
  Copy,
  Check,
  Download,
  ArrowLeft,
} from "lucide-react";

const SHARE_URL = "https://kursosonline.com";
const SHARE_TITLE = "Aprende lo que amas, en español 🚀";
const SHARE_TEXT =
  "Acabo de descubrir Kursos Online: 40 cursos premium en español sobre finanzas, negocios, IA y marketing. Garantía de 7 días.";

const channels = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    Icon: MessageCircle,
    color: "#25D366",
    href: () =>
      `https://api.whatsapp.com/send?text=${encodeURIComponent(
        `${SHARE_TITLE}\n\n${SHARE_TEXT}\n\n${SHARE_URL}`,
      )}`,
  },
  {
    id: "facebook",
    label: "Facebook",
    Icon: Facebook,
    color: "#1877F2",
    href: () =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        SHARE_URL,
      )}&quote=${encodeURIComponent(SHARE_TEXT)}`,
  },
  {
    id: "twitter",
    label: "X / Twitter",
    Icon: Twitter,
    color: "#0F1419",
    href: () =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `${SHARE_TEXT}`,
      )}&url=${encodeURIComponent(SHARE_URL)}`,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    Icon: Linkedin,
    color: "#0A66C2",
    href: () =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        SHARE_URL,
      )}`,
  },
  {
    id: "telegram",
    label: "Telegram",
    Icon: Send,
    color: "#229ED9",
    href: () =>
      `https://t.me/share/url?url=${encodeURIComponent(
        SHARE_URL,
      )}&text=${encodeURIComponent(`${SHARE_TITLE}\n${SHARE_TEXT}`)}`,
  },
  {
    id: "email",
    label: "Email",
    Icon: Mail,
    color: "#4F46E5",
    href: () =>
      `mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodeURIComponent(
        `${SHARE_TEXT}\n\n${SHARE_URL}`,
      )}`,
  },
];

const Share = () => {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: SHARE_TITLE,
          text: SHARE_TEXT,
          url: SHARE_URL,
        });
      } catch (err) {
        // user cancelled — silent
        if (err.name !== "AbortError") console.error(err);
      }
    } else {
      copyLink();
    }
  };

  return (
    <main data-testid="share-page" className="bg-zinc-50 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <Link
          to="/"
          className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>

        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#4F46E5] mb-3">
          Kit para compartir
        </p>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-4">
          Comparte Kursos Online
          <br />
          <span className="text-[#4F46E5]">y conviértete en embajador</span>
        </h1>
        <p className="text-zinc-600 text-lg max-w-2xl mb-12">
          Comparte este enlace o el banner oficial en tus redes. Cada persona
          que se inscriba puede ser un nuevo cliente para ti.
        </p>

        {/* Banner preview */}
        <div className="mb-10 bg-white border border-zinc-200 overflow-hidden shadow-sm">
          <img
            src="/og-image.png"
            alt="Banner social Kursos Online"
            data-testid="share-banner-preview"
            className="w-full block"
          />
          <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-zinc-200">
            <p className="text-sm text-zinc-600">
              Banner oficial · 1200×630 px · Perfecto para Facebook, WhatsApp,
              LinkedIn y X.
            </p>
            <a
              href="/og-image.png"
              download="kursos-online-banner.png"
              data-testid="download-banner-btn"
              className="inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-black text-white px-5 py-2.5 font-semibold text-sm transition"
            >
              <Download className="w-4 h-4" /> Descargar banner
            </a>
          </div>
        </div>

        {/* Copy link block */}
        <div className="bg-white border border-zinc-200 p-5 md:p-6 mb-10 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">
              Tu enlace para compartir
            </p>
            <p
              data-testid="share-link"
              className="font-mono text-base md:text-lg text-zinc-900 truncate"
            >
              {SHARE_URL}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={copyLink}
              data-testid="copy-link-btn"
              className="inline-flex items-center gap-2 border border-zinc-300 hover:border-zinc-900 px-4 py-2.5 font-semibold text-sm transition"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-600" /> Copiado
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copiar
                </>
              )}
            </button>
            <button
              onClick={nativeShare}
              data-testid="native-share-btn"
              className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-4 py-2.5 font-semibold text-sm transition"
            >
              Compartir
            </button>
          </div>
        </div>

        {/* Channel grid */}
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">
          O elige una red
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-12">
          {channels.map((c) => {
            const Icon = c.Icon;
            return (
              <a
                key={c.id}
                href={c.href()}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`share-${c.id}-btn`}
                className="group bg-white border border-zinc-200 hover:border-zinc-900 p-5 flex flex-col items-center gap-3 transition"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-transform group-hover:scale-110"
                  style={{ backgroundColor: c.color }}
                >
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <span className="text-sm font-semibold">{c.label}</span>
              </a>
            );
          })}
        </div>

        {/* Pre-written messages */}
        <div className="bg-zinc-900 text-white p-6 md:p-10">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#22D3EE] mb-3">
            Mensajes listos para copiar
          </p>
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-6">
            Plantillas para tus redes
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                title: "WhatsApp / Telegram",
                text: `🎓 ¡Encontré una escuela digital increíble!\n\nKursos Online tiene 40 cursos en español sobre finanzas, IA, e-commerce y marketing — con garantía de 7 días.\n\nMira el catálogo:\n${SHARE_URL}`,
              },
              {
                title: "Instagram / Stories",
                text: `40 cursos en español para emprendedores 🇲🇽🇦🇷🇨🇴🇪🇸\n\nFinanzas · IA · E-commerce · Marketing\n\n👉 ${SHARE_URL}`,
              },
              {
                title: "Facebook / LinkedIn",
                text: `Estoy aprendiendo en Kursos Online, una escuela digital con 40 cursos premium en español. Ideal para emprendedores LATAM. Tiene garantía de 7 días y comunidad activa. Te lo recomiendo: ${SHARE_URL}`,
              },
              {
                title: "Email a un amigo",
                text: `Hola,\n\nQuería compartirte esto: Kursos Online (${SHARE_URL}). Es una plataforma con 40 cursos en español sobre temas como trading, IA y e-commerce. Hay garantía de 7 días así que se puede probar sin riesgo.\n\nUn abrazo.`,
              },
            ].map((tpl) => (
              <TemplateCard key={tpl.title} title={tpl.title} text={tpl.text} />
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

const TemplateCard = ({ title, text }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <div className="bg-zinc-800/60 border border-zinc-700 p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wider font-bold text-zinc-400">
          {title}
        </p>
        <button
          onClick={copy}
          data-testid={`copy-template-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className="text-xs font-semibold text-[#22D3EE] hover:text-white inline-flex items-center gap-1"
        >
          {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {done ? "Copiado" : "Copiar"}
        </button>
      </div>
      <p className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed">
        {text}
      </p>
    </div>
  );
};

export default Share;
